import BigNumValue from "./BigNumValue";
import BigNum from "../math/BigNum";
import NumberValue from "./NumberValue";
import GameModel from "../../GameModel";
import BooleanValue from "./BooleanValue";

export default class Values extends GameModel {
    constructor() {
        super();

        /////////////////////////////////////////////////////////////////////
        // UPGRADES
        /////////////////////////////////////////////////////////////////////

        /**
         * Connected to Pick Upgrades.
         * @constant
         * @type {BigNumValue}
         */
        this.baseAdditiveBonusPerTurn = new BigNumValue('baseAdditiveBonusPerTurn', new BigNum(0));

        /**
         * Connected to Pick Upgrades.
         * @constant
         * @type {BigNumValue}
         */
        this.baseMultiplicativeBonusValue = new BigNumValue('baseMultiBonusValue', new BigNum(0));

        // (base_pick_power + mine_additive + pick_additive) * (mine_multiplicative + pick_multiplicative)

        /**
         * Connected to Mined Damage Upgrades.
         * @constant
         * @type {BigNumValue}
         */
        this.minedDamageBonusMultiplier = new BigNumValue('minedDamageBonusValue', new BigNum(0));
        /**
         * Connected to Mined Ore Upgrades.
         * @constant
         * @type {BigNumValue}
         */
        this.minedOreBonus = new BigNumValue('minedOreBonusValue', new BigNum(0));


        /////////////////////////////////////////////////////////////////////
        // BONUSES
        /////////////////////////////////////////////////////////////////////

        /**
         * @constant
         * @type {NumberValue}
         */
        this.bonusOreMultiplier = new NumberValue(null, 0);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.bonusUpgradeMultiplier = new NumberValue(null, 0);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.bonusGoldIncrement = new NumberValue(null, 0);

        
        /////////////////////////////////////////////////////////////////////
        // GOLD
        /////////////////////////////////////////////////////////////////////

        /**
         * @constant
         * @type {BigNumValue}
         */
        this.goldOreBonusMultiplier = new BigNumValue(null, new BigNum(1));

        /**
         * @constant
         * @type {BigNumValue}
         */
        this.goldPerBlockBonus = new BigNumValue(null, new BigNum(0));

        /////////////////////////////////////////////////////////////////////
        // SKILLS
        /////////////////////////////////////////////////////////////////////

        /**
         * @constant
         * @type {BigNumValue}
         */
        this.skillOreBonusMultiplier = new BigNumValue(null, new BigNum(0));
        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillPrestigeBonusMultiplier = new NumberValue(null, 0); // current damage bonus from upgrades
        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillRubyBonusMultiplier = new NumberValue(null, 0); // current damage bonus from upgrades
        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillBlockBonusMultiplier = new NumberValue(null, 0); // current damage bonus from upgrades
        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillDepthBonusMultiplier = new NumberValue(null, 0);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillUpgradeAdditiveBonusMultiplier = new NumberValue(null, 0);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillUpgradeMultiplicativeBonusMultiplier = new NumberValue(null, 0);

        /**
         * @constant
         * @type {NumberValue}
         */
        this.fallSpeedUpgrades = new NumberValue(null, 0);

        /**
         * Fall speed is only active in certain situations, so we need a flag to control this.
         * @constant
         * @type {BooleanValue}
         */
        this.fallSpeedBonusActive = new BooleanValue(null, false);

        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillMinedDamageBonus = new NumberValue(null, 0);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.skillMinedOreBonusUpgrades = new NumberValue(null, 0);

        /**
         * @constant
         * @type {BooleanValue}
         */
        this.minedAutomationUnlocked = new BooleanValue(null, false);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.minedAutomationRateUpgrades = new NumberValue(null, 0);
        /**
         * @constant
         * @type {BooleanValue}
         */
        this.pickAutomationUnlocked = new BooleanValue(null, false);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.pickAutomationRateUpgrades = new NumberValue(null, 0);
        /**
         * @constant
         * @type {BooleanValue}
         */
        this.prestigeAutomationUnlocked = new BooleanValue(null, false);
        /**
         * @constant
         * @type {NumberValue}
         */
        this.prestigeAutomationRateUpgrades = new NumberValue(null, 0);

        /**
         * @constant
         * @type {NumberValue}
         */
        this.offlineTimeUpgrades = new NumberValue(null, 0);

    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {

        /////////////////////////////////////////////////////////////////////
        // RESET UPGRADES & BONUSES via resetForPrestige()
        /////////////////////////////////////////////////////////////////////

        this.resetForPrestige();


        /////////////////////////////////////////////////////////////////////
        // GOLD BONUSES
        /////////////////////////////////////////////////////////////////////

        this.goldOreBonusMultiplier.reset();
        this.goldPerBlockBonus.reset();

        /////////////////////////////////////////////////////////////////////
        // SKILLS
        /////////////////////////////////////////////////////////////////////

        this.skillOreBonusMultiplier.reset();
        this.skillPrestigeBonusMultiplier.reset();
        this.skillRubyBonusMultiplier.reset();
        this.skillBlockBonusMultiplier.reset();
        this.skillDepthBonusMultiplier.reset();
        this.fallSpeedUpgrades.reset();
        
        this.skillUpgradeAdditiveBonusMultiplier.reset();
        this.skillUpgradeMultiplicativeBonusMultiplier.reset();
        // this.skillMinedDamageBonusUpgrades.reset();
        this.skillMinedDamageBonus.reset();
        this.skillMinedOreBonusUpgrades.reset();

        this.minedAutomationUnlocked.reset();
        this.minedAutomationRateUpgrades.reset();
        
        this.pickAutomationUnlocked.reset();
        this.pickAutomationRateUpgrades.reset();

        this.prestigeAutomationUnlocked.reset();
        this.prestigeAutomationRateUpgrades.reset();
        this.offlineTimeUpgrades.reset();

    }

    /**
     * @override
     */
    resetForPrestige() {
        /////////////////////////////////////////////////////////////////////
        // UPGRADES
        /////////////////////////////////////////////////////////////////////

        this.baseAdditiveBonusPerTurn.reset();
        this.baseMultiplicativeBonusValue.reset();
        this.minedDamageBonusMultiplier.reset();
        this.minedOreBonus.reset();

        /////////////////////////////////////////////////////////////////////
        // BONUSES
        /////////////////////////////////////////////////////////////////////

        this.bonusOreMultiplier.reset();
        this.bonusUpgradeMultiplier.reset();
        this.bonusGoldIncrement.reset();

        /////////////////////////////////////////////////////////////////////
        // SKILLS
        /////////////////////////////////////////////////////////////////////

        // Don't clear the skill values here.
    }
}